if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        if (this.elements.viewer) {
          // Enable infinite slider looping so arrows never get disabled
          this.elements.viewer.enableSliderLooping = true;
          this.initGalleryNavigation();
        }

        if (this.elements.thumbnails) {
          this.initThumbnails();
        }

        if (this.dataset.desktopLayout && this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) {
          this.removeListSemantic();
        }
      }

      initGalleryNavigation() {
        // Prev and Next floating arrow buttons
        const prevBtns = this.querySelectorAll('.ferreti-gallery-arrow--prev, [id^="GalleryViewer"] button[name="previous"]');
        const nextBtns = this.querySelectorAll('.ferreti-gallery-arrow--next, [id^="GalleryViewer"] button[name="next"]');

        prevBtns.forEach((btn) => {
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.navigateSlide(-1);
          });
        });

        nextBtns.forEach((btn) => {
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.navigateSlide(1);
          });
        });

        // Touch & Tap navigation directly over the main image
        let touchStartX = 0;
        let touchStartY = 0;
        let isTouchDrag = false;

        this.elements.viewer.addEventListener(
          'touchstart',
          (e) => {
            if (e.touches && e.touches.length === 1) {
              touchStartX = e.touches[0].clientX;
              touchStartY = e.touches[0].clientY;
              isTouchDrag = false;
            }
          },
          { passive: true }
        );

        this.elements.viewer.addEventListener(
          'touchmove',
          (e) => {
            if (e.touches && e.touches.length === 1) {
              const dx = Math.abs(e.touches[0].clientX - touchStartX);
              const dy = Math.abs(e.touches[0].clientY - touchStartY);
              if (dx > 12 || dy > 12) {
                isTouchDrag = true;
              }
            }
          },
          { passive: true }
        );

        this.elements.viewer.addEventListener('click', (e) => {
          if (isTouchDrag) return;
          // Ignore clicks on buttons, links, inputs, or zoom triggers
          if (
            e.target.closest(
              'button, a, input, select, .ferreti-gallery-arrow, .ferreti-gallery-zoom-button, .deferred-media__poster-button, .slider-button'
            )
          ) {
            return;
          }

          const rect = this.elements.viewer.getBoundingClientRect();
          const clickX = e.clientX - rect.left;
          // Left 35% advances backward, right 65% advances forward
          if (clickX < rect.width * 0.35) {
            this.navigateSlide(-1);
          } else {
            this.navigateSlide(1);
          }
        });

        // Keyboard navigation (Left / Right keys)
        this.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowLeft') {
            this.navigateSlide(-1);
          } else if (e.key === 'ArrowRight') {
            this.navigateSlide(1);
          }
        });

        // Sync slide change on native touch swipe
        const slider = this.elements.viewer.querySelector('[id^="Slider-Gallery"]');
        if (slider) {
          slider.addEventListener(
            'scroll',
            debounce(() => {
              this.onScrollSync();
            }, 60)
          );
        }
      }

      initThumbnails() {
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const btn = mediaToSwitch.querySelector('button');
          if (!btn) return;
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            this.setActiveMedia(mediaToSwitch.dataset.target, false);
          });
        });
      }

      navigateSlide(direction) {
        const slides = Array.from(this.elements.viewer.querySelectorAll('.product__media-item'));
        if (slides.length <= 1) return;

        let currentIndex = slides.findIndex((slide) => slide.classList.contains('is-active'));
        if (currentIndex === -1) {
          const slider = this.elements.viewer.querySelector('[id^="Slider-Gallery"]');
          const scrollLeft = slider ? slider.scrollLeft : 0;
          const slideWidth = slides[0].offsetWidth || 1;
          currentIndex = Math.round(scrollLeft / slideWidth);
        }

        const nextIndex = (currentIndex + direction + slides.length) % slides.length;
        const targetSlide = slides[nextIndex];
        if (targetSlide) {
          this.setActiveMedia(targetSlide.dataset.mediaId, false);
        }
      }

      onScrollSync() {
        const slides = Array.from(this.elements.viewer.querySelectorAll('.product__media-item'));
        if (slides.length <= 1) return;
        const slider = this.elements.viewer.querySelector('[id^="Slider-Gallery"]');
        if (!slider) return;

        const scrollLeft = slider.scrollLeft;
        const slideWidth = slides[0].offsetWidth || 1;
        const activeIndex = Math.min(slides.length - 1, Math.max(0, Math.round(scrollLeft / slideWidth)));
        const activeSlide = slides[activeIndex];

        if (activeSlide && !activeSlide.classList.contains('is-active')) {
          slides.forEach((s) => s.classList.remove('is-active'));
          activeSlide.classList.add('is-active');

          this.updateCounter(activeIndex + 1);
          this.updateZoomTarget(activeSlide);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(
              `[data-target="${activeSlide.dataset.mediaId}"]`
            );
            this.setActiveThumbnail(activeThumbnail);
          }
        }
      }

      onSlideChanged(event) {
        if (!event.detail || !event.detail.currentElement) return;
        const thumbnail = this.elements.thumbnails?.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) return;

        const slides = Array.from(this.elements.viewer.querySelectorAll('[data-media-id]'));
        slides.forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        const activeIndex = slides.indexOf(activeMedia);
        if (activeIndex >= 0) {
          this.updateCounter(activeIndex + 1);
        }
        this.updateZoomTarget(activeMedia);

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail &&
              activeThumbnail.parentElement.firstChild !== activeThumbnail &&
              activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();

        const slider = activeMedia.parentElement;
        if (slider) {
          slider.scrollTo({
            left: activeMedia.offsetLeft,
            behavior: 'smooth',
          });
        }

        this.playActiveMedia(activeMedia);

        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          this.setActiveThumbnail(activeThumbnail);
          if (activeThumbnail) {
            this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
          }
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        // Clear active classes from all items and buttons
        this.elements.thumbnails.querySelectorAll('.thumbnail-list__item').forEach((item) => {
          item.classList.remove('is-active');
        });
        this.elements.thumbnails.querySelectorAll('button').forEach((element) => {
          element.removeAttribute('aria-current');
        });

        // Set active state on target thumbnail li and button
        thumbnail.classList.add('is-active');
        const btn = thumbnail.querySelector('button');
        if (btn) btn.setAttribute('aria-current', 'true');

        // Smoothly center the active thumbnail in view
        const thumbsSlider = this.elements.thumbnails.querySelector('.thumbnail-list') || this.elements.thumbnails;
        if (thumbsSlider && thumbsSlider.scrollWidth > thumbsSlider.clientWidth) {
          const scrollTarget = thumbnail.offsetLeft - thumbsSlider.clientWidth / 2 + thumbnail.clientWidth / 2;
          thumbsSlider.scrollTo({
            left: Math.max(0, scrollTarget),
            behavior: 'smooth',
          });
        }
      }

      updateCounter(current) {
        const counterCur = this.querySelector('.ferreti-counter-current') || this.querySelector('.slider-counter--current');
        if (counterCur) counterCur.textContent = current;
      }

      updateZoomTarget(activeSlide) {
        const zoomBtn = this.querySelector('.ferreti-gallery-zoom-button');
        if (zoomBtn && activeSlide) {
          const pureId = activeSlide.dataset.mediaId?.split('-').pop();
          if (pureId) zoomBtn.setAttribute('data-media-id', pureId);
        }
      }

      announceLiveRegion(activeItem, position) {
        const image =
          activeItem.querySelector('.product__media img') ||
          activeItem.querySelector('.product__modal-opener--image img') ||
          activeItem.querySelector('img');
        if (!image) return;
        image.onload = () => {
          if (this.elements.liveRegion) {
            this.elements.liveRegion.setAttribute('aria-hidden', false);
            this.elements.liveRegion.innerHTML = (window.accessibilityStrings?.imageAvailable || 'Image [index]').replace(
              '[index]',
              position
            );
            setTimeout(() => {
              this.elements.liveRegion.setAttribute('aria-hidden', true);
            }, 2000);
          }
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        if (typeof window.pauseAllMedia === 'function') {
          window.pauseAllMedia();
        }
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia && typeof deferredMedia.loadContent === 'function') {
          deferredMedia.loadContent(false);
        }
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        if (this.elements.viewer.sliderItems) {
          this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
        }
      }
    }
  );
}
